import React, { useRef } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import PixelButton from '../ui/PixelButton.jsx';
import MonkeyFooter from '../ui/MonkeyFooter.jsx';

gsap.registerPlugin(useGSAP);

const HERO_SRC = '/images/hero-kaiju.webp';

export default function TitleScreen({
  onNewGame,
  onOpenCodex,
  user,
  isAuthenticated,
  onOpenAuth,
  onSignOut,
}) {
  const scope = useRef(null);
  const heroRef = useRef(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();

      // Reduced motion gets the static layout (elements render visible at rest).
      mm.add('(prefers-reduced-motion: no-preference)', () => {
        const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

        tl.from('.t-letterhead', { yPercent: -120, opacity: 0, duration: 0.5 })
          .from('.t-title', { y: 34, opacity: 0, duration: 0.6 }, '-=0.15')
          .from(
            '.t-stamp',
            { scale: 2.4, rotate: -34, opacity: 0, duration: 0.5, ease: 'back.out(2.2)' },
            '-=0.2'
          )
          .from('.t-tagline', { y: 18, opacity: 0, duration: 0.45 }, '-=0.15')
          .from('.t-sub', { y: 14, opacity: 0, duration: 0.4 }, '-=0.3')
          .from(
            heroRef.current,
            { xPercent: 14, y: 40, opacity: 0, scale: 1.05, duration: 0.9, ease: 'power2.out' },
            '-=0.85'
          )
          .from('.t-menu > *', { y: 16, opacity: 0, duration: 0.4, stagger: 0.09 }, '-=0.55')
          .from('.t-fineprint', { opacity: 0, duration: 0.5 }, '-=0.2')
          .from('.t-footer', { opacity: 0, duration: 0.5 }, '-=0.3');

        // Idle: the boss breathes; the corner stamps drift.
        gsap.to(heroRef.current, {
          y: '-=14',
          duration: 3.4,
          ease: 'sine.inOut',
          repeat: -1,
          yoyo: true,
        });
        gsap.to('.t-ambient', {
          rotate: '+=2.5',
          duration: 5,
          ease: 'sine.inOut',
          repeat: -1,
          yoyo: true,
          stagger: 0.6,
        });
      });
    },
    { scope }
  );

  return (
    <div ref={scope} className="relative">
      {/* Main hero section — locked to viewport height, content lives here */}
      <section className="relative h-screen overflow-hidden flex flex-col paper-crumpled">
      {/* Ambient filigree stamps — texture, not content */}
      <div className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
        <span className="t-ambient absolute top-[12%] left-[5%] rotate-[-16deg] text-stamp-red/15 border-4 border-stamp-red/15 px-5 py-1.5 font-display font-extrabold text-2xl uppercase">
          Refusé
        </span>
        <span className="t-ambient absolute bottom-[20%] left-[7%] rotate-[7deg] text-brown/15 border-4 border-brown/15 px-4 py-1 font-display font-extrabold text-lg uppercase">
          Urgent
        </span>
        <span className="t-ambient absolute top-[46%] left-[2%] rotate-[-38deg] text-mustard-dark/15 border-4 border-mustard-dark/15 px-3 py-0.5 font-display font-extrabold text-base uppercase">
          Payé
        </span>
      </div>

      {/* Hero boss — mobile: looms from the top edge, fading down into the title.
         Tablet+: bleeds off the right. Desktop: full-height bleed with right-edge mask. */}
      <img
        ref={heroRef}
        src={HERO_SRC}
        alt="Un kaiju géant fait de dossiers administratifs tamponnés « Refusé » menace un petit citoyen-guerrier armé d'un tampon."
        fetchpriority="high"
        className="
          pointer-events-none select-none absolute z-0
          top-0 right-[-8%] h-[44vh] w-auto max-w-none opacity-55
          [mask-image:linear-gradient(to_bottom,#000_45%,transparent_96%)]
          sm:top-auto sm:bottom-0 sm:right-[-6%] sm:h-[68vh] sm:opacity-90
          sm:[mask-image:linear-gradient(to_top,transparent_4%,#000_46%)]
          lg:right-[-2%] lg:h-[94vh] lg:opacity-100
          lg:[mask-image:linear-gradient(to_right,transparent_1%,#000_24%)]
        "
      />

      {/* Foreground content */}
      <div className="relative z-10 flex-1 w-full max-w-6xl mx-auto px-5 sm:px-8 flex flex-col">
        {/* Letterhead */}
        <header className="t-letterhead flex items-center justify-between gap-3 border-b-2 border-ink/70 py-3">
          <p className="font-stamp text-[11px] sm:text-sm tracking-widest text-brown uppercase leading-none">
            République Administrative · Dossier N° 00-666
          </p>
          <span className="font-display font-extrabold text-[10px] sm:text-xs uppercase tracking-widest text-stamp-green border-2 border-stamp-green px-2 py-0.5 rotate-[4deg]">
            Reçu
          </span>
        </header>

        {/* Main: left-weighted, asymmetric */}
        <div className="flex-1 flex flex-col justify-center py-8 max-w-xl">
          {/* Title + slammed stamp */}
          <div className="relative inline-block self-start mb-5">
            <h1 className="t-title font-display text-6xl sm:text-7xl lg:text-8xl font-extrabold text-ink leading-[0.82] tracking-tighter text-shadow-ink">
              ALLOC
              <br />
              WARRIOR
            </h1>
            <span
              className="t-stamp absolute -right-6 sm:-right-10 bottom-2 rotate-[-11deg] border-[5px] border-stamp-red text-stamp-red bg-paper/70 px-3 py-1 font-display font-extrabold text-xl sm:text-2xl uppercase tracking-widest"
              aria-hidden="true"
            >
              Refusé
            </span>
          </div>

          {/* Tagline */}
          <p className="t-tagline self-start bg-ink text-paper font-display font-bold text-sm sm:text-base uppercase tracking-wide px-3 py-1.5 -rotate-1 shadow-neo-sm mb-3">
            Le RPG où la France est le boss final
          </p>
          <p className="t-sub font-sans text-xs sm:text-sm text-brown italic mb-9">
            — manuel de survie administrative, en 30 nœuds et 5 fins —
          </p>

          {/* Menu */}
          <div className="t-menu flex flex-col items-start gap-3">
            <PixelButton onClick={onNewGame} size="lg" variant="danger" pulse>
              ▸ Nouvelle Partie
            </PixelButton>

            <div className="flex flex-wrap items-center gap-2.5">
              <PixelButton onClick={onOpenCodex} size="md" variant="ghost">
                📋 Codex
              </PixelButton>

              {isAuthenticated ? (
                <PixelButton onClick={onSignOut} size="md" variant="ghost">
                  Se déconnecter
                </PixelButton>
              ) : (
                <PixelButton onClick={() => onOpenAuth('signin')} size="md" variant="ghost">
                  👤 Connexion
                </PixelButton>
              )}
            </div>

            {isAuthenticated && (
              <span className="font-sans text-[11px] text-brown">
                Connecté : <span className="font-bold text-ink">{user?.email}</span>
              </span>
            )}
          </div>

          {/* Fine print */}
          <div className="t-fineprint mt-10 flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="inline-block rotate-[-3deg] border-2 border-dashed border-brown px-3 py-1 font-stamp text-sm text-brown leading-none">
              Cerfa n° 00-666
            </span>
            <p className="font-sans text-[10px] text-outline leading-relaxed">
              v1.0 — aucun fonctionnaire n'a été blessé.
              <br className="hidden sm:block" /> (par contre, 3 dossiers ont été perdus)
            </p>
          </div>
        </div>
      </div>

      </section>

      {/* Network footer — scrolled into view below the hero */}
      <div className="t-footer relative z-10 w-full max-w-5xl mx-auto">
        <MonkeyFooter />
      </div>
    </div>
  );
}
